import { useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AtaImagem } from '@/services/ataImagem.service';

interface AtaGaleriaPreviewProps {
  imagens: AtaImagem[];
}

export function AtaGaleriaPreview({ imagens }: AtaGaleriaPreviewProps) {
  const { t } = useTranslation('common');
  const [lightboxImage, setLightboxImage] = useState<AtaImagem | null>(null);
  
  if (!imagens || imagens.length === 0) {
    return null;
  }

  return (
    <>
      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="galeria-lightbox fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightboxImage.url}
            alt={lightboxImage.descricao || 'Imagem ampliada'}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Seção de Galeria - Layout único simples */}
      <section className="galeria-section mt-8">
        <h2 className="galeria-titulo text-base font-semibold text-gray-900 mb-4 pb-2 border-b">
          {t('photographic_record')}
        </h2>
        
        {/* Grid único - funciona em tela e impressão */}
        <table className="galeria-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {/* Renderiza 1 imagem gigante por página */}
            {imagens.map((imagem, imgIndex) => (
              <tr key={imagem.id} className="galeria-img-row" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <td 
                  style={{ 
                    width: '100%', 
                    padding: '0', 
                    verticalAlign: 'top',
                  }}
                >
                  <div 
                    className="galeria-img-container"
                    onClick={() => setLightboxImage(imagem)}
                    style={{ cursor: 'pointer', pageBreakInside: 'avoid', breakInside: 'avoid' }}
                  >
                    <img
                      src={imagem.url}
                      alt={imagem.descricao || `Imagem ${imgIndex + 1}`}
                      style={{ 
                        width: '100%', 
                        height: '700px',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                    <p style={{ 
                      margin: '16px 0 0 0',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: '#1f2937',
                      fontWeight: '500',
                    }}>
                      <strong>{imgIndex + 1}.</strong> {imagem.descricao || '—'}
                    </p>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <style>{`
        @media print {
          .galeria-lightbox {
            display: none !important;
          }
          .galeria-section {
            page-break-inside: avoid;
          }
          .galeria-img-row {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-after: always !important;
            break-after: page !important;
          }
          .galeria-img-container {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .galeria-table img {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        
        @media screen {
          .galeria-img-container:hover img {
            opacity: 0.9;
          }
        }
      `}</style>
    </>
  );
}
